// Shared exam-run canvas — the polished question view used by the Aptitude
// drill, Daily Focus, and Weekly Mock. Each page keeps its own data/logic and
// recap; this renders the focused question → options → answer-reveal surface
// with a calm "exam paper" aesthetic (see ExamRun.css).
//
// Preserves the contracts the e2e suite depends on:
//   - a [role="progressbar"] with aria-valuenow = questions answered
//   - each option contains a <span> whose text is exactly "A." (letter + dot)
//   - feedback copy "Correct." / "Not quite — answer is X." + Next/Finish

import React, { useEffect, useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import MarkdownRenderer from '../MicroLearning/MarkdownRenderer';
import MediaDisplay, { MediaItem } from '../MicroLearning/MediaDisplay';
import TranslateAmharicButton from '../MicroLearning/TranslateAmharicButton';
import './ExamRun.css';

const fmtDur = (s: number) =>
  s < 60 ? `${s}s` : `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

export interface ExamAccent {
  accent: string;
  soft: string;
  ring: string;
  ink: string;
  /** Dark-mode remap of the same four vars (light soft/ink read as invisible
      or bright patches on the dark card). Optional so custom accents still
      fall back to the light values. */
  dark?: { accent: string; soft: string; ring: string; ink: string };
}

export const ACCENTS: Record<'aptitude' | 'daily' | 'mock', ExamAccent> = {
  aptitude: {
    accent: '#4a7c10', soft: '#ecfdf5', ring: '#bbf7d0', ink: '#166534',
    dark: { accent: '#86c232', soft: 'rgba(134, 194, 50, 0.16)', ring: 'rgba(134, 194, 50, 0.4)', ink: '#a5d65a' },
  },
  daily: {
    accent: '#1d4ed8', soft: '#eff6ff', ring: '#bfdbfe', ink: '#1e40af',
    dark: { accent: '#5b8def', soft: 'rgba(91, 141, 239, 0.16)', ring: 'rgba(91, 141, 239, 0.4)', ink: '#9ab8f5' },
  },
  mock: {
    accent: '#6d28d9', soft: '#f5f3ff', ring: '#ddd6fe', ink: '#5b21b6',
    dark: { accent: '#a78bfa', soft: 'rgba(167, 139, 250, 0.16)', ring: 'rgba(167, 139, 250, 0.4)', ink: '#c4b5fd' },
  },
};

interface Opt {
  label: string;
  text: string;
}

interface LastAnswer {
  is_correct: boolean;
  correct_answer: string;
  explanation?: string;
}

export interface QuizCanvasProps {
  accent?: ExamAccent;
  onBack: () => void;
  answered: number;
  qNumber: number;
  total: number;
  rightSlot?: React.ReactNode;
  contextLabel?: string;
  questionText: string;
  /**
   * The stimulus this question is asked ABOUT — a reading passage, an ordering
   * scenario, a chart — rendered above the stem and owned by the caller.
   *
   * A slot rather than a string because the thing a caller needs here is
   * behaviour, not text: a passage shared by five questions has to stay put
   * while the student moves between them, and whether it is expanded is the
   * caller's state, not this component's. Optional, so every existing caller
   * renders byte for byte as before.
   */
  stimulus?: React.ReactNode;
  media?: MediaItem[];
  options: Opt[];
  selected: string | null;
  lastAnswer: LastAnswer | null;
  submitting: boolean;
  onSelect: (label: string) => void;
  onNext: () => void;
  isLast: boolean;
  nextLabel?: string;
  finishLabel?: string;
  optionTestId?: (label: string) => string;
  feedbackTestId?: string;
  runTestId?: string;
  aiHelp?: React.ReactNode;
  /** Stable id for the current question — keys the Amharic translation cache. */
  questionId?: string;
  /** Telemetry surface label for the 🇪🇹 translate button (per run type). */
  translateSurface?: string;

  /**
   * Sit this question as part of a real paper rather than as practice.
   *
   * Practice and an exam want opposite things from the same screen. Practice
   * teaches: commit, see the answer, understand it, move on — which is why
   * everything below `lastAnswer` exists. An exam measures: you answer, you
   * may change your mind, you move on knowing nothing, and you find out at
   * the end. Revealing the answer mid-paper does not make the sitting kinder,
   * it makes the score meaningless.
   *
   * So exam mode changes three things and nothing else: options stay live
   * after a selection, no feedback block is ever rendered, and navigation
   * moves into a footer that is always present rather than appearing only
   * once you have answered. Default off — every existing caller keeps the
   * practice behaviour byte for byte.
   */
  examMode?: boolean;
  /** Exam mode only: go back. Absent on the first question of a section. */
  onPrev?: () => void;
  /** Exam mode only: mark for review. Flagged items are listed at the end. */
  flagged?: boolean;
  onFlag?: () => void;
}

const QuizCanvas: React.FC<QuizCanvasProps> = ({
  accent = ACCENTS.daily,
  onBack,
  answered,
  qNumber,
  total,
  rightSlot,
  contextLabel,
  questionText,
  stimulus,
  media,
  options,
  selected,
  lastAnswer,
  submitting,
  onSelect,
  onNext,
  isLast,
  nextLabel = 'Next question →',
  finishLabel = 'Finish',
  optionTestId,
  feedbackTestId,
  runTestId,
  aiHelp,
  questionId,
  translateSurface = 'quiz_explanation',
  examMode = false,
  onPrev,
  flagged = false,
  onFlag,
}) => {
  // Per-question stopwatch — ticks while the question is open, freezes on
  // answer, resets on the next question. Drives the header timer (when the
  // page doesn't supply its own, e.g. the mock countdown) and the
  // "Answered in Xs" feedback line.
  const [qElapsed, setQElapsed] = useState(0);
  useEffect(() => {
    setQElapsed(0);
  }, [questionText]);
  useEffect(() => {
    if (lastAnswer) return;
    const id = window.setInterval(() => setQElapsed((e) => e + 1), 1000);
    return () => window.clearInterval(id);
  }, [lastAnswer, questionText]);

  const { theme } = useTheme();
  const dark = theme === 'dark';
  const a = dark && accent.dark ? accent.dark : accent;

  const pct = total > 0 ? Math.round((answered / total) * 100) : 0;
  const rootStyle = {
    ['--er-accent' as any]: a.accent,
    ['--er-accent-soft' as any]: a.soft,
    ['--er-accent-ring' as any]: a.ring,
    ['--er-accent-ink' as any]: a.ink,
  } as React.CSSProperties;

  return (
    <div className="er-root" style={rootStyle}>
      <header className="er-header">
        <button className="er-back" onClick={onBack} aria-label="Back">
          ←
        </button>
        <div className="er-railwrap">
          <div
            className="er-rail"
            role="progressbar"
            aria-valuenow={answered}
            aria-valuemin={0}
            aria-valuemax={total}
          >
            <div className="er-rail-fill" style={{ width: `${pct}%` }} />
          </div>
          <div className="er-count">Question {qNumber} of {total}</div>
        </div>
        <div className="er-right">
          {rightSlot || <span className="er-stopwatch">⏱ {fmtDur(qElapsed)}</span>}
        </div>
      </header>

      <main className="er-main">
        <div className="er-card" data-testid={runTestId}>
          {contextLabel && <div className="er-context">{contextLabel}</div>}
          {/* Above the stem, because the question is asked about it — reading
              "what is the main idea of the passage?" before the passage is
              backwards, and on a phone it is the only order that puts the
              question next to the options. */}
          {stimulus}
          {/* Long stems (English reading passages etc.) read as a compact
              passage block; short questions keep the elegant serif. */}
          <div className={`er-q${questionText.length > 360 ? ' er-q--long' : ''}`}>
            <MarkdownRenderer>{questionText}</MarkdownRenderer>
          </div>

          {media && media.length > 0 && (
            <div className="er-media">
              <MediaDisplay media={media} maxHeight={320} />
            </div>
          )}

          <div className="er-options">
            {options.map((opt) => {
              let state: string | undefined;
              if (lastAnswer && !examMode) {
                if (opt.label === lastAnswer.correct_answer) state = 'correct';
                else if (opt.label === selected && !lastAnswer.is_correct) state = 'wrong';
              } else if (opt.label === selected) {
                state = 'selected';
              }
              return (
                <button
                  key={opt.label}
                  className="er-opt"
                  data-state={state}
                  data-testid={optionTestId ? optionTestId(opt.label) : undefined}
                  onClick={() => onSelect(opt.label)}
                  // In an exam an answer is provisional until the section
                  // closes — locking the first tap would punish exactly the
                  // care the paper is meant to reward.
                  disabled={examMode ? submitting : !!lastAnswer || submitting}
                >
                  <span className="er-badge">{opt.label}.</span>
                  <span className="er-opt-text">
                    <MarkdownRenderer>{opt.text}</MarkdownRenderer>
                  </span>
                </button>
              );
            })}
          </div>

          {examMode && (
            <div className="er-navbar">
              <button
                className="er-nav er-nav--ghost"
                onClick={onPrev}
                disabled={!onPrev}
              >
                ← Previous
              </button>
              {onFlag && (
                <button
                  className="er-nav er-nav--flag"
                  data-on={flagged || undefined}
                  onClick={onFlag}
                  aria-pressed={flagged}
                >
                  {flagged ? '🚩 Flagged' : '⚑ Flag'}
                </button>
              )}
              <button className="er-nav" onClick={onNext}>
                {isLast ? finishLabel : nextLabel}
              </button>
            </div>
          )}

          {lastAnswer && !examMode && (
            <div className="er-feedback" data-ok={lastAnswer.is_correct} data-testid={feedbackTestId}>
              <div className="er-fb-title">
                {lastAnswer.is_correct
                  ? 'Correct.'
                  : `Not quite — answer is ${lastAnswer.correct_answer}.`}
              </div>
              {lastAnswer.explanation && (
                <div className="er-explain">
                  <MarkdownRenderer>{lastAnswer.explanation}</MarkdownRenderer>
                  {/* 🇪🇹 በአማርኛ — re-renders the explanation code-switched, keeping
                      the English above so students stay anchored to exam terms. */}
                  <TranslateAmharicButton
                    surface={translateSurface}
                    cacheKey={questionId || lastAnswer.explanation.slice(0, 64)}
                    text={lastAnswer.explanation}
                    trackProps={{ channel: 'web', question_id: questionId }}
                  />
                </div>
              )}
              <div className="er-fb-time">⏱ Answered in {fmtDur(qElapsed)}</div>
              <button className="er-next" onClick={onNext}>
                {isLast ? finishLabel : nextLabel}
              </button>
            </div>
          )}
        </div>

        {aiHelp}
      </main>
    </div>
  );
};

export default QuizCanvas;
